import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "apply", "reset", "results", "status", "resultCount", "voiceInput", "voiceButton", "voiceStatus"]

  connect() {
    this.submitting = false
    this.initialState = this.serializedState()
    this.syncStatus()
    this.updateApplyState()
    this.setupVoiceInput()
  }

  disconnect() {
    this.recognition?.abort()
  }

  changed() {
    if (this.submitting) return

    this.updateApplyState()
  }

  submitStarted() {
    this.submitting = true
    this.resultsTarget.style.minHeight = `${this.resultsTarget.offsetHeight}px`
    this.applyTarget.disabled = true
    this.resetTarget.disabled = true
    this.applyTarget.setAttribute("aria-busy", "true")
  }

  submitFinished(event) {
    if (!event.detail.success) {
      this.finishLoading()
      return
    }

    const responseUrl = event.detail.fetchResponse?.response?.url
    if (responseUrl) {
      const url = new URL(responseUrl)
      const internalParams = ["entry_filter_anchor", "entry_view_mode", "commit"]
      internalParams.forEach((name) => url.searchParams.delete(name))
      url.hash = "entry-filters"
      window.history.replaceState(window.history.state, "", url)
    }
  }

  frameLoaded(event) {
    if (event.target !== this.resultsTarget) return

    this.syncStatus()
    this.initialState = this.serializedState()
    this.finishLoading()
    this.restoreSelectedView()
  }

  reset(event) {
    event.preventDefault()

    Array.from(this.formTarget.elements).forEach((field) => {
      if (!field.name || ["entry_filter_anchor", "entry_view_mode"].includes(field.name)) return

      if (field.type === "range") {
        field.value = field.name.endsWith("_min") ? field.min : field.max
      } else if (["select-one", "hidden", "text", "search"].includes(field.type) || field.tagName === "TEXTAREA") {
        field.value = ""
      }

      field.dispatchEvent(new Event("input", { bubbles: true }))
      field.dispatchEvent(new Event("change", { bubbles: true }))
    })

    this.formTarget.requestSubmit()
  }

  serializedState() {
    const ignoredNames = new Set(["entry_filter_anchor", "entry_view_mode"])

    return Array.from(new FormData(this.formTarget).entries())
      .filter(([name]) => !ignoredNames.has(name))
      .map(([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
      .sort()
      .join("&")
  }

  updateApplyState() {
    const isDirty = this.serializedState() !== this.initialState
    this.applyTarget.classList.toggle("is-primary", isDirty)
    this.applyTarget.dataset.dirty = isDirty ? "true" : "false"
  }

  syncStatus() {
    if (!this.hasResultCountTarget) return

    this.statusTarget.textContent = this.resultCountTarget.dataset.resultText
  }

  finishLoading() {
    this.submitting = false
    this.applyTarget.disabled = false
    this.resetTarget.disabled = false
    this.applyTarget.removeAttribute("aria-busy")
    this.resultsTarget.style.minHeight = ""
    this.updateApplyState()
  }

  restoreSelectedView() {
    const selectedView = this.formTarget.querySelector("input[name='entry_view_mode']:checked")
    selectedView?.dispatchEvent(new Event("change", { bubbles: true }))
  }

  setupVoiceInput() {
    if (!this.hasVoiceInputTarget || !this.hasVoiceButtonTarget) return

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      this.voiceButtonTarget.disabled = true
      this.voiceButtonTarget.title = "Voice input is not supported in this browser"
      this.updateVoiceStatus("Voice input is not supported in this browser.")
      return
    }

    this.recognition = new SpeechRecognition()
    this.recognition.continuous = false
    this.recognition.interimResults = true
    this.recognition.lang = navigator.language || "en-US"
    this.recognition.onstart = () => this.voiceStarted()
    this.recognition.onresult = (event) => this.voiceResult(event)
    this.recognition.onerror = (event) => this.voiceFailed(event)
    this.recognition.onend = () => this.voiceEnded()
  }

  toggleVoice() {
    if (!this.recognition) return

    if (this.voiceListening) {
      this.recognition.stop()
      return
    }

    this.voiceBaseValue = this.voiceInputTarget.value.trim()
    this.voiceErrorMessage = null

    try {
      this.recognition.start()
    } catch (_error) {
      this.updateVoiceStatus("Voice input is already active.")
    }
  }

  voiceStarted() {
    this.voiceListening = true
    this.voiceButtonTarget.classList.add("is-listening")
    this.voiceButtonTarget.setAttribute("aria-pressed", "true")
    this.voiceButtonTarget.setAttribute("aria-label", "Stop voice input")
    this.voiceButtonTarget.title = "Listening… select to stop"
    this.updateVoiceStatus("Listening. Describe the home you are looking for.")
  }

  voiceResult(event) {
    const transcript = Array.from(event.results)
      .map((result) => result[0].transcript.trim())
      .filter(Boolean)
      .join(" ")

    this.voiceInputTarget.value = [this.voiceBaseValue, transcript].filter(Boolean).join(" ")
    this.voiceInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  voiceFailed(event) {
    const messages = {
      "audio-capture": "No microphone was found.",
      "no-speech": "No speech was detected. Try again.",
      "not-allowed": "Microphone access was blocked.",
      "service-not-allowed": "Microphone access was blocked."
    }

    this.voiceErrorMessage = messages[event.error] || "Voice input stopped. Try again."
    this.updateVoiceStatus(this.voiceErrorMessage)
  }

  voiceEnded() {
    this.voiceListening = false
    this.voiceButtonTarget.classList.remove("is-listening")
    this.voiceButtonTarget.setAttribute("aria-pressed", "false")
    this.voiceButtonTarget.setAttribute("aria-label", "Describe your search by voice")
    this.voiceButtonTarget.title = "Describe your search by voice"

    if (!this.voiceErrorMessage) {
      this.updateVoiceStatus(this.voiceInputTarget.value.trim() ? "Voice description added to search." : "Voice input stopped.")
    }
  }

  updateVoiceStatus(message) {
    if (this.hasVoiceStatusTarget) this.voiceStatusTarget.textContent = message
  }
}
